import './ProductSnapshotCard.scss';

import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { shape, array, bool, string, func, object, number } from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import BaseCard from 'components/shared/Card/BaseCard';
import {
  Percent,
  Dollar,
  FormattedDate,
  EvaluatedPercent
} from 'components/shared/Text/Text';
import ValueIndicator from 'components/shared/ValueIndicator/ValueIndicator';
import {
  getProducts,
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  isPershingProduct
} from 'services/Products';
import {
  dailyChangeAmount,
  dailyChangeRate,
  totalBalance
} from 'services/Calculation';
import { NON_BREAKING_SPACE, plusMinusValue } from 'services/Formats';
import Content from 'services/Content';
import CardDisclosureModal from 'components/shared/Disclosure/CardDisclosureModal';
import { activePlusMyAccountsUrl } from 'services/Externals';
import {
  getCustomerPlanningGroupId,
  getCustomerWebView,
  getUberId,
  isActiveHousehold
} from 'state/customer/CustomerSelectors';
import { hasActiveAdvisory } from 'state/view/ViewSelectors';
import IntradayRefresh from 'components/shared/Intraday/IntradayRefresh';
import { intradayRefreshEnabled } from 'services/FeatureFlags';
import {
  getIntradayRefreshEnabled,
  hasIntradayBrokerageAccounts
} from 'state/account/AccountSelectors';
import { filterAccountsWithAsOfDate } from 'services/Filters';
import { getAsOfDateFromList, intradayEligibleFilter } from 'services/Accounts';
import IntradayAsOfDate from 'components/shared/Intraday/IntradayAsOfDate';
import BalanceAsOfDate from 'components/shared/AsOfDate/BalanceAsOfDate';
import WorkplaceRetirementBalanceDisclosure from 'components/shared/WorkplaceRetirementBalanceDisclosure/WorkplaceRetirementBalanceDisclosure';

export const getShowModelName = (showPortfolio, name) => {
  return Boolean(showPortfolio && name);
};

export const getShowPreferredPortfolioName = (
  showPortfolio,
  name,
  preferredName
) => {
  return Boolean(showPortfolio && !name && preferredName);
};

export const areAsOfDatesEqual = (date1, date2) => {
  if (!date1 && !date2) {
    return true;
  }

  if ((!date1 && date2) || (date1 && !date2)) {
    return false;
  }
  return date1 === date2;
};

export const getActivePlusHasRateOfReturn = (
  productType,
  accounts,
  prrData
) => {
  return isActivePlus(productType) && (accounts.length > 1 || isEmpty(prrData));
};

export const getPRRDataByKey = (data, index, key, defaultValue) => {
  if (!key || !data || (index && index < 0)) {
    return null;
  }
  return data[index] && data[index][key] ? data[index][key] : defaultValue;
};

export const getHasActiveHousehold = (
  accounts,
  activeHousehold,
  customerGroupId
) => {
  return (
    activeHousehold &&
    accounts.some(
      (account) => account.financialPlanningGroupId === customerGroupId
    )
  );
};

export const getShowProductCard = (
  accounts,
  productType,
  hasActiveHousehold,
  balance
) => {
  return (
    !isEmpty(accounts) &&
    (!isActiveAdvisory(productType) || hasActiveHousehold || balance !== 0)
  );
};

function ProductSnapshotCard({
  title,
  footer,
  hideTitleBar,
  showIntradayRefresh,
  showCurrentPortfolio,
  data,
  prrData,
  isCWV,
  uberId,
  customerGroupId,
  isActiveHousehold,
  hasIntradayAccounts
}) {
  const { accounts, modelName, asOfDate, preferredPortfolioName } = data;

  const balance = totalBalance(accounts);
  const dailyChange = dailyChangeAmount(accounts);
  const rateChange = dailyChangeRate(accounts);
  const showModelName = getShowModelName(showCurrentPortfolio, modelName);
  const showPreferredPortfolioName = getShowPreferredPortfolioName(
    showCurrentPortfolio,
    modelName,
    preferredPortfolioName
  );
  const accountsWithAsOfDate = filterAccountsWithAsOfDate(accounts);
  const balanceAsOfDate = getAsOfDateFromList(accountsWithAsOfDate, 0);
  const productType = getProducts(accounts).find(
    (p) => p != null && typeof p != 'undefined'
  );

  const showBalanceAsOfDate = !areAsOfDatesEqual(asOfDate, balanceAsOfDate);

  const checkBalanceAsOfDate =
    isPershingProduct(productType) && balanceAsOfDate;
  // Show view return link if PRR data unavailable or if more than one APP account
  const showActivePlusViewReturn = getActivePlusHasRateOfReturn(
    productType,
    accounts,
    prrData
  );
  const inceptionReturn = getPRRDataByKey(prrData, 0, 'inceptionReturn', null);
  const inceptionDate = getPRRDataByKey(prrData, 0, 'inceptionDate', null);
  const hasActiveHousehold = getHasActiveHousehold(
    accounts,
    isActiveHousehold,
    customerGroupId
  );

  const showCard = getShowProductCard(
    accounts,
    productType,
    hasActiveHousehold,
    balance
  );
  const showIntradayRefeshIcon =
    showIntradayRefresh && intradayEligibleFilter({ productType, balance });
  return (
    <Fragment>
      {showCard && (
        <BaseCard
          title={null}
          isLoading={false}
          className="standard product-snapshot-card-container"
          footer={footer}>
          <Fragment>
            {!hideTitleBar && (
              <div className="top">
                <div className="title-container">
                  <h4 className="title">{title}</h4>
                </div>
                <div className="as-of-date">
                  {checkBalanceAsOfDate &&
                  intradayRefreshEnabled() &&
                  isBrokerage(productType) &&
                  hasIntradayAccounts ? (
                    <IntradayAsOfDate
                      productType={productType}
                      asOfDate={balanceAsOfDate}
                    />
                  ) : checkBalanceAsOfDate && showBalanceAsOfDate ? (
                    <BalanceAsOfDate
                      balanceAsOfDate={balanceAsOfDate}
                      productType={productType}
                    />
                  ) : null}
                  {showIntradayRefeshIcon && (
                    <IntradayRefresh
                      label="Refresh"
                      containerClassName="refresh-brokerage-table"
                    />
                  )}
                </div>
                {showModelName && (
                  <div className="current-portfolio">
                    Current Portfolio: {modelName}
                  </div>
                )}
                {showPreferredPortfolioName && (
                  <div className="current-portfolio">
                    Current Portfolio: {preferredPortfolioName}
                  </div>
                )}
              </div>
            )}
            <div className="bottom container">
              <div className="balance">
                <h6 className="label">Balance</h6>
                <div className="value">
                  <Dollar className="balance-value">{balance}</Dollar>
                </div>
              </div>
              <div className="daily-change-prr-col">
                <div className="daily-change">
                  <h6 className="label">Daily Change</h6>
                  <div className="value">
                    <Dollar>{dailyChange}</Dollar>
                    <div className="rate-change">
                      <span className="small">
                        {NON_BREAKING_SPACE}(
                        <Percent formatters={[plusMinusValue]}>
                          {rateChange}
                        </Percent>
                        )
                      </span>
                      <ValueIndicator value={rateChange} />
                    </div>
                  </div>
                </div>
                {(inceptionReturn || showActivePlusViewReturn) && (
                  <div className="prr">
                    <h6 className="label">
                      PRR Since Inception
                      <CardDisclosureModal
                        className="restricted-height-modal"
                        hideIcon={false}
                        hideLinkContent
                        content={Content.prrPerformanceDisclosure}
                      />
                    </h6>
                    {showActivePlusViewReturn ? (
                      <a
                        href={activePlusMyAccountsUrl(isCWV, uberId)}
                        className="app-prr-link">
                        View Return
                      </a>
                    ) : (
                      <Fragment>
                        <EvaluatedPercent element="div" className="value">
                          {inceptionReturn ? inceptionReturn / 100 : ''}
                        </EvaluatedPercent>
                        <FormattedDate
                          className="inception-date"
                          format="MMMM DD, YYYY">
                          {inceptionDate}
                        </FormattedDate>
                      </Fragment>
                    )}
                  </div>
                )}
              </div>
            </div>
            <WorkplaceRetirementBalanceDisclosure accounts={accounts} />
          </Fragment>
        </BaseCard>
      )}
    </Fragment>
  );
}

ProductSnapshotCard.defaultProps = {
  data: {
    accounts: []
  },
  hideTitleBar: false,
  prrData: []
};

ProductSnapshotCard.propTypes = {
  hasActiveAdvisory: bool,
  title: string,
  footer: object.isRequired,
  data: shape({
    accounts: array.isRequired,
    asOfDate: string,
    modelName: string,
    preferredPortfolioName: string
  }),
  onRefresh: func,
  hideTitleBar: bool,
  showCurrentPortfolio: bool,
  prrData: array,
  isCWV: bool,
  uberId: string,
  customerGroupId: number,
  isActiveHousehold: bool
};

const mapStateToProps = (state) => {
  return {
    hasActiveAdvisory: hasActiveAdvisory(state),
    showIntradayRefresh: getIntradayRefreshEnabled(state),
    isCWV: getCustomerWebView(state),
    uberId: getUberId(state),
    customerGroupId: getCustomerPlanningGroupId(state),
    isActiveHousehold: isActiveHousehold(state),
    hasIntradayAccounts: hasIntradayBrokerageAccounts(state)
  };
};

export default connect(mapStateToProps)(ProductSnapshotCard);
